// Comprehensive FAQ Database for AI-Powered Chat
// Includes questions from Services, About, Pricing, FAQ, and Community sections
// Total: 50+ questions covering all aspects of the website

export const faqDatabase = [
  // Process Excellence Category
  {
    id: 1,
    category: "process",
    question: "What is Lean Manufacturing?",
    keywords: ["lean", "manufacturing", "waste", "efficiency", "kaizen", "5s", "value stream"],
    answer: "Lean Manufacturing is a systematic approach to eliminating waste and maximizing value in production processes. It focuses on continuous improvement (Kaizen), reducing the 8 types of waste, implementing 5S workplace organization, and creating efficient value streams. We've helped clients reduce cycle times by 30-40% while improving quality.",
    relatedQuestions: [2, 3, 69],
    cta: { text: "View Lean Services", link: "/pricing" }
  },
  {
    id: 2,
    category: "process",
    question: "How does Six Sigma differ from Lean?",
    keywords: ["six sigma", "lean", "difference", "compare", "methodology", "dmaic", "quality"],
    answer: "While Lean focuses on eliminating waste and improving flow, Six Sigma focuses on reducing variation and defects using statistical methods. Lean is about speed and efficiency, while Six Sigma is about quality and consistency. Many organizations use both together (Lean Six Sigma) for comprehensive improvement. Lean uses tools like Kaizen and 5S, while Six Sigma uses DMAIC and statistical analysis.",
    relatedQuestions: [1, 42],
    cta: { text: "Compare Pricing", link: "/pricing" }
  },
  {
    id: 3,
    category: "process",
    question: "What is the DMAIC methodology?",
    keywords: ["dmaic", "define", "measure", "analyze", "improve", "control", "six sigma", "methodology"],
    answer: "DMAIC is the core Six Sigma methodology: Define (project goals), Measure (current performance), Analyze (root causes), Improve (implement solutions), Control (sustain improvements). It's a data-driven approach that ensures sustainable results. We've completed 60+ DMAIC projects with 95% success rate.",
    relatedQuestions: [2, 42],
    cta: { text: "Book Six Sigma Consultation", link: "/book-consultation" }
  },
  {
    id: 4,
    category: "process",
    question: "What ROI can I expect from process improvement?",
    keywords: ["roi", "return", "investment", "savings", "benefits", "results", "value"],
    answer: "Typical ROI ranges from 300-500% within the first year. Common benefits include: 30-40% reduction in cycle time, 20-30% cost savings, 40-50% improvement in productivity, and 50-60% reduction in defects. Actual results depend on project scope and current baseline performance.",
    relatedQuestions: [1, 2, 99],
    cta: { text: "View Case Studies", link: "/#services" }
  },

  // Compliance & Risk Category
  {
    id: 5,
    category: "compliance",
    question: "Which ISO standards do you specialize in?",
    keywords: ["iso", "standards", "9001", "27001", "42001", "quality", "information security", "ai management"],
    answer: "We specialize in ISO 9001:2015 (Quality Management), ISO 27001:2022 (Information Security), and ISO 42001 (AI Management Systems). Our Lead Auditor credentials ensure comprehensive implementation and audit readiness. We've achieved 100% first-time certification success with zero major non-conformities.",
    relatedQuestions: [6, 7, 46],
    cta: { text: "View ISO Services", link: "/pricing" }
  },
  {
    id: 6,
    category: "compliance",
    question: "How long does ISO certification typically take?",
    keywords: ["iso", "certification", "timeline", "duration", "how long", "time", "months"],
    answer: "ISO certification projects typically take 6-12 months depending on organizational readiness and standard complexity. The process includes gap analysis (1-2 months), documentation (2-3 months), implementation (3-4 months), internal audit (1 month), and certification audit (1 month). We've achieved 100% first-time certification success.",
    relatedQuestions: [5, 7],
    cta: { text: "Book ISO Assessment", link: "/book-consultation" }
  },
  {
    id: 7,
    category: "compliance",
    question: "What is your approach to risk management?",
    keywords: ["risk", "management", "assessment", "mitigation", "strategy", "compliance"],
    answer: "We follow a comprehensive risk assessment methodology covering operational, financial, compliance, and strategic risks. Our approach includes: 1) Risk identification and mapping, 2) Impact and probability analysis, 3) Mitigation strategy development, 4) Control implementation, and 5) Ongoing monitoring frameworks. We use both qualitative and quantitative methods.",
    relatedQuestions: [5, 6],
    cta: { text: "Learn More", link: "/#services" }
  },

  // AI & Technology Category
  {
    id: 8,
    category: "technology",
    question: "What types of processes are suitable for RPA?",
    keywords: ["rpa", "automation", "robotic process", "suitable", "processes", "automate"],
    answer: "RPA works best with rule-based, repetitive processes involving structured data. Ideal candidates include: invoice processing, data entry, report generation, compliance monitoring, customer onboarding, and email management. We've successfully automated 60% of manual tasks in typical implementations, achieving 99%+ accuracy rates.",
    relatedQuestions: [9, 10, 15],
    cta: { text: "View AI Services", link: "/pricing" }
  },
  {
    id: 9,
    category: "technology",
    question: "How do you ensure AI systems are compliant and ethical?",
    keywords: ["ai", "compliance", "ethical", "governance", "iso 42001", "responsible ai"],
    answer: "We implement AI governance frameworks based on ISO 42001 standards, ensuring transparency, accountability, and bias mitigation. Our approach includes: AI risk assessment, ethical AI principles implementation, bias detection and correction, explainability frameworks, and continuous monitoring. We ensure compliance with data privacy regulations and industry standards.",
    relatedQuestions: [8, 10, 15],
    cta: { text: "View AI Governance", link: "/pricing" }
  },
  {
    id: 10,
    category: "technology",
    question: "What is the ROI timeline for automation projects?",
    keywords: ["roi", "automation", "timeline", "return", "payback", "savings"],
    answer: "Most automation projects achieve ROI within 6-12 months. Typical benefits include: 40-90% reduction in manual effort, 99%+ accuracy rates, 24/7 processing capability, and significant cost savings. Our recent projects have delivered 300-400% ROI within the first year. Initial investment is typically recovered through labor cost savings and efficiency gains.",
    relatedQuestions: [8, 9],
    cta: { text: "Calculate Your ROI", link: "/book-consultation" }
  },

  // Pricing & Investment Category
  {
    id: 11,
    category: "pricing",
    question: "How do you structure your pricing?",
    keywords: ["pricing", "structure", "cost", "fees", "payment", "models"],
    answer: "We offer flexible engagement models: 1) Project-based fixed pricing (most popular), 2) Hourly consulting rates (₹1,500-₹4,000/hour depending on service), 3) Success-based fees tied to measurable outcomes. Most clients prefer project-based pricing for predictable budgeting and clear deliverables. We also offer phased payment options.",
    relatedQuestions: [12, 13],
    cta: { text: "View Detailed Pricing", link: "/pricing" }
  },
  {
    id: 12,
    category: "pricing",
    question: "What is the typical investment range for projects?",
    keywords: ["investment", "cost", "price", "range", "budget", "expense"],
    answer: "Investment varies by service and scope. Consultation: ₹15,000-₹90,000 | Training: ₹20,000-₹1,50,000 | Audit: ₹50,000-₹2,40,000 | Implementation: ₹1,00,000-₹1,80,000. We currently offer 50-70% limited-time discounts on implementation services. Custom packages available based on your specific needs.",
    relatedQuestions: [11, 13, 15, 42, 43, 44, 45, 46],
    cta: { text: "View Full Pricing", link: "/pricing" }
  },
  {
    id: 13,
    category: "pricing",
    question: "Do you offer payment plans or phased payments?",
    keywords: ["payment", "plan", "phased", "installment", "milestone", "schedule"],
    answer: "Yes, we structure payments based on project milestones and deliverables. Typical structure: 30% upfront, 40% at mid-project milestone, and 30% upon completion. This ensures alignment with project progress and client cash flow. For larger projects, we can customize payment schedules to match your budget cycles.",
    relatedQuestions: [11, 12],
    cta: { text: "Discuss Payment Options", link: "/book-consultation" }
  },

  // Gen AI & Agentic AI Pricing
  {
    id: 15,
    category: "pricing",
    question: "How much does Gen AI & Agentic AI Strategy cost?",
    keywords: ["gen ai", "agentic ai", "ai strategy", "cost", "price", "artificial intelligence", "iso 42001"],
    answer: "Gen AI & Agentic AI Strategy pricing: Hourly Rate: ₹2,000-₹4,000 | Consultation: ₹20,000-₹90,000 | Audit: ₹1,00,000-₹2,40,000 | Training: ₹40,000-₹1,50,000 | Implementation: ₹1,50,000-₹1,80,000 (50-70% discount from ₹3,00,000-₹6,00,000). Includes AI Governance Framework, ISO 42001 Compliance, AI Risk Management, Ethical AI Implementation, and AI Performance Monitoring.",
    relatedQuestions: [9, 12, 42],
    cta: { text: "Book AI Strategy Call", link: "/book-consultation" }
  },

  // Six Sigma Pricing
  {
    id: 42,
    category: "pricing",
    question: "How much does Six Sigma Implementation cost?",
    keywords: ["six sigma", "cost", "price", "dmaic", "green belt", "black belt", "training"],
    answer: "Six Sigma Implementation pricing: Hourly Rate: ₹1,500-₹3,500 | Consultation: ₹15,000-₹60,000 | Audit: ₹50,000-₹1,20,000 | Training: ₹20,000-₹80,000 | Implementation: ₹1,00,000-₹1,08,000 (50-70% discount from ₹2,00,000-₹3,60,000). Includes DMAIC Methodology, Statistical Process Control, Quality Management Systems, Defect Reduction, and Process Optimization.",
    relatedQuestions: [2, 3, 12, 15],
    cta: { text: "Get Six Sigma Quote", link: "/book-consultation" }
  },

  // Lean Process Excellence Pricing
  {
    id: 43,
    category: "pricing",
    question: "What does Lean Process Excellence cost?",
    keywords: ["lean", "process excellence", "cost", "price", "kaizen", "5s", "value stream mapping"],
    answer: "Lean Process Excellence pricing: Hourly Rate: ₹1,500-₹3,500 | Consultation: ₹15,000-₹60,000 | Audit: ₹50,000-₹1,20,000 | Training: ₹20,000-₹80,000 | Implementation: ₹1,00,000-₹1,08,000 (50-70% discount from ₹2,00,000-₹3,60,000). Includes Value Stream Mapping, Waste Elimination, Continuous Flow Design, Kaizen Implementation, and 5S Workplace Organization.",
    relatedQuestions: [1, 12, 69],
    cta: { text: "Get Lean Quote", link: "/book-consultation" }
  },

  // Operational Excellence Pricing
  {
    id: 44,
    category: "pricing",
    question: "How much is Operational Excellence Management?",
    keywords: ["operational excellence", "opex", "cost", "price", "management", "performance"],
    answer: "Operational Excellence Management pricing: Hourly Rate: ₹2,000-₹3,500 | Consultation: ₹20,000-₹72,000 | Audit: ₹75,000-₹1,80,000 | Training: ₹30,000-₹1,20,000 | Implementation: ₹1,25,000-₹1,35,000 (50-70% discount from ₹2,50,000-₹4,50,000). Comprehensive approach covering strategy alignment, culture transformation, performance management, and continuous improvement.",
    relatedQuestions: [12, 15, 42],
    cta: { text: "Learn More", link: "/pricing" }
  },

  // ISO Certification Pricing
  {
    id: 45,
    category: "pricing",
    question: "What is the cost of ISO Certification services?",
    keywords: ["iso", "certification", "cost", "price", "9001", "27001", "42001", "audit"],
    answer: "ISO Certification pricing: Hourly Rate: ₹1,500-₹3,000 | Consultation: ₹15,000-₹54,000 | Audit: ₹50,000-₹1,50,000 | Training: ₹20,000-₹1,00,000 | Implementation: ₹1,00,000-₹1,08,000 (50-70% discount from ₹2,00,000-₹3,60,000). Covers ISO 9001, ISO 27001, ISO 42001. Includes gap analysis, documentation, implementation support, and audit preparation.",
    relatedQuestions: [5, 6, 12],
    cta: { text: "Get ISO Quote", link: "/book-consultation" }
  },

  // RPA & Automation Pricing
  {
    id: 46,
    category: "pricing",
    question: "What does RPA & Intelligent Automation cost?",
    keywords: ["rpa", "automation", "robotic process", "cost", "price", "bot"],
    answer: "RPA & Intelligent Automation pricing: Hourly Rate: ₹2,000-₹4,000 | Consultation: ₹20,000-₹90,000 | Audit: ₹1,00,000-₹2,00,000 | Training: ₹40,000-₹1,20,000 | Implementation: ₹1,50,000-₹1,62,000 (50-70% discount from ₹3,00,000-₹5,40,000). Includes process assessment, bot development, testing, deployment, and maintenance support.",
    relatedQuestions: [8, 10, 12],
    cta: { text: "Get Automation Quote", link: "/book-consultation" }
  },

  // Industries & Experience
  {
    id: 69,
    category: "general",
    question: "Which industries do you serve?",
    keywords: ["industries", "sectors", "experience", "manufacturing", "banking", "technology", "pharma"],
    answer: "We serve 7+ industries with 21+ years of experience: Manufacturing, Banking & Financial Services, Technology & IT Services, Supply Chain & Logistics, Title Insurance, Market Research, and Advertising & Creative Production. We've completed 60+ projects across these sectors with proven results and industry-specific expertise.",
    relatedQuestions: [1, 4],
    cta: { text: "View Industry Experience", link: "/#about" }
  },

  // General Information
  {
    id: 99,
    category: "general",
    question: "How do I get started with your services?",
    keywords: ["get started", "begin", "start", "how to", "first step", "consultation"],
    answer: "Getting started is easy: 1) Book a free 30-minute strategy call to discuss your needs, 2) We'll conduct a preliminary assessment and provide recommendations, 3) Receive a customized proposal with scope, timeline, and investment, 4) Upon agreement, we kick off the project with dedicated support. You can book a consultation online or call us at +91 99625 36379.",
    relatedQuestions: [11, 12],
    cta: { text: "Book Free Consultation", link: "/book-consultation" }
  },

  // Contact & Support
  {
    id: 100,
    category: "general",
    question: "How can I contact you?",
    keywords: ["contact", "reach", "phone", "email", "whatsapp", "call"],
    answer: "You can reach us multiple ways: Phone: +91 99625 36379 | WhatsApp: Click the WhatsApp button on our website | Email: Through our contact form | LinkedIn: Connect with us professionally | Book Online: Schedule a consultation directly through our booking page. We respond within 2 hours during business hours.",
    relatedQuestions: [99],
    cta: { text: "Contact Us Now", link: "/#contact" }
  },

  // ABOUT SECTION - Consultant Background & Expertise
  {
    id: 101,
    category: "about",
    question: "What is your professional background and experience?",
    keywords: ["background", "experience", "credentials", "qualification", "about", "consultant", "shanso"],
    answer: "I bring 21+ years of comprehensive consulting experience across process excellence, AI/automation, and compliance. Key credentials: Lead Auditor (ISO 9001, 27001, 42001), Six Sigma Black Belt, Lean Champion, Strategic Performance Consultant. I've successfully completed 60+ projects serving 7+ industries including manufacturing, banking, technology, and supply chain with consistent 95% success rate.",
    relatedQuestions: [102, 103, 69],
    cta: { text: "Learn More About Me", link: "/#about" }
  },
  {
    id: 102,
    category: "about",
    question: "What certifications do you hold?",
    keywords: ["certifications", "certified", "credentials", "qualifications", "lead auditor", "black belt"],
    answer: "Professional Certifications: ISO 9001:2015 Lead Auditor, ISO 27001:2022 Lead Auditor, ISO 42001 (AI Management) Lead Auditor, Six Sigma Black Belt, Lean Manufacturing Champion, Risk Management Professional, Strategic Performance & Transformation Consultant. All certifications are current and internationally recognized.",
    relatedQuestions: [101, 103, 5],
    cta: { text: "View Credentials", link: "/#about" }
  },
  {
    id: 103,
    category: "about",
    question: "What are your key achievements?",
    keywords: ["achievements", "success", "results", "track record", "accomplishments"],
    answer: "Key Achievements: ✅ 60+ Successful Projects Completed, ✅ 500+ Professionals Trained (Six Sigma, Lean, ISO), ✅ 95% Project Success Rate, ✅ 21+ Years Industry Experience, ✅ 7+ Industries Served, ✅ 100% First-time ISO Certification Success, ✅ Zero Major Non-conformities in Audits, ✅ Average 300-500% ROI for clients.",
    relatedQuestions: [101, 102, 4],
    cta: { text: "See Case Studies", link: "/#services" }
  },
  {
    id: 104,
    category: "about",
    question: "What makes your consulting approach unique?",
    keywords: ["unique", "approach", "methodology", "different", "special", "why choose"],
    answer: "My unique approach combines: 1) Practical Expertise - 21+ years of hands-on implementation, not just theory, 2) Holistic Solutions - Integration of process excellence, technology, and compliance, 3) Proven Results - 95% success rate with measurable ROI, 4) Industry-Specific Knowledge - Deep experience in 7+ sectors, 5) Sustainable Implementation - Focus on long-term capability building, not just project delivery, 6) Personal Involvement - Direct engagement throughout the project lifecycle.",
    relatedQuestions: [101, 103, 99],
    cta: { text: "Schedule Strategy Call", link: "/book-consultation" }
  },

  // SERVICES SECTION - Detailed Service Questions
  {
    id: 105,
    category: "services",
    question: "What does your Process Excellence service include?",
    keywords: ["process excellence", "service", "include", "deliver", "scope", "what do you do"],
    answer: "Process Excellence includes: Lean Manufacturing Implementation (5S, Kaizen, Value Stream Mapping), Six Sigma Projects (DMAIC methodology, Green/Black Belt training), Process Optimization (workflow redesign, bottleneck elimination), Performance Metrics Design (KPIs, dashboards), Change Management (stakeholder engagement, training), and Continuous Improvement Systems. Deliverables include documented processes, trained teams, and sustainable improvement frameworks.",
    relatedQuestions: [1, 2, 43],
    cta: { text: "View Process Services", link: "/pricing" }
  },
  {
    id: 106,
    category: "services",
    question: "What AI and automation services do you offer?",
    keywords: ["ai services", "automation services", "what ai", "what automation", "ai automation"],
    answer: "AI & Automation Services: Generative AI Strategy (use case identification, pilot implementation), Agentic AI Systems (autonomous agents, workflow automation), RPA Implementation (process assessment, bot development, deployment), AI Governance (ISO 42001 compliance, ethical AI frameworks), Intelligent Document Processing, Chatbot Development, Predictive Analytics, and AI-Powered Decision Support Systems. Full lifecycle support from strategy to deployment.",
    relatedQuestions: [8, 9, 15, 46],
    cta: { text: "Explore AI Services", link: "/pricing" }
  },
  {
    id: 107,
    category: "services",
    question: "Do you provide training programs?",
    keywords: ["training", "courses", "certification", "workshop", "learning", "education"],
    answer: "Yes! Comprehensive training programs: Six Sigma (Green Belt, Black Belt certification courses), Lean Manufacturing (practitioner workshops, Kaizen events), ISO Standards (9001, 27001, 42001 implementation training), AI & Automation (Gen AI, RPA training), Risk Management, and Process Excellence Fundamentals. All programs include hands-on exercises, real-world case studies, certification preparation, and post-training support. Customized corporate training available.",
    relatedQuestions: [102, 42, 43, 45],
    cta: { text: "View Training Options", link: "/pricing" }
  },
  {
    id: 108,
    category: "services",
    question: "What industries have you worked with?",
    keywords: ["industries", "sectors", "experience", "worked with", "clients", "manufacturing", "banking"],
    answer: "7+ Industries Served: 1) Manufacturing (automotive, pharma, consumer goods), 2) Banking & Financial Services (risk management, process optimization), 3) Technology & IT Services (software delivery, DevOps), 4) Supply Chain & Logistics (warehouse optimization, transportation), 5) Title Insurance (process standardization, compliance), 6) Market Research (data analytics, automation), 7) Advertising & Creative Production (workflow optimization). Industry-specific methodologies and best practices applied to each sector.",
    relatedQuestions: [69, 101, 103],
    cta: { text: "View Industry Experience", link: "/#about" }
  },
  {
    id: 109,
    category: "services",
    question: "Do you work with small businesses or only enterprises?",
    keywords: ["small business", "startup", "enterprise", "company size", "sme", "large company"],
    answer: "I work with organizations of ALL sizes! Services are tailored to your scale: Startups & SMEs: Foundational process frameworks, lean startup methodologies, scalable systems design, affordable engagement models. Mid-Market: Comprehensive process improvement, department-level transformations, certification preparation. Enterprises: Large-scale transformations, multi-site implementations, enterprise-wide compliance programs. Flexible pricing and phased approaches ensure accessibility for all business sizes.",
    relatedQuestions: [11, 12, 99],
    cta: { text: "Discuss Your Needs", link: "/book-consultation" }
  },
  {
    id: 110,
    category: "services",
    question: "What is included in a consultation session?",
    keywords: ["consultation", "session", "what happens", "initial call", "discovery", "assessment"],
    answer: "Free 30-minute consultation includes: 1) Current State Assessment (discuss your challenges and goals), 2) Solution Recommendation (tailored approach based on your needs), 3) Scope & Timeline Discussion (realistic project planning), 4) ROI Projection (expected benefits and payback period), 5) Q&A Session (address all your questions), 6) Custom Proposal (if interested in moving forward). No obligation, no pressure - just valuable insights.",
    relatedQuestions: [99, 11, 12],
    cta: { text: "Book Free Consultation", link: "/book-consultation" }
  },

  // PRICING - Additional Pricing Questions
  {
    id: 111,
    category: "pricing",
    question: "Why are your implementation prices discounted?",
    keywords: ["discount", "why discount", "sale", "offer", "reduced price", "limited time"],
    answer: "Limited-Time Opportunity: We're offering 50-70% discounts on implementation services to build our portfolio with diverse case studies and expand our client base. This is a genuine value opportunity - full services at significantly reduced rates. Original prices: ₹2L-₹6L, now ₹1L-₹1.8L. All deliverables, quality, and support remain unchanged. Available for serious implementations only, not just consulting or training.",
    relatedQuestions: [12, 13, 99],
    cta: { text: "View Current Offers", link: "/pricing" }
  },
  {
    id: 112,
    category: "pricing",
    question: "What's the difference between consultation and implementation?",
    keywords: ["consultation vs implementation", "difference", "what is consultation", "what is implementation"],
    answer: "Consultation (₹15K-₹90K): Advisory services including assessment, recommendations, roadmap creation, strategy sessions, expert guidance, and documentation. You execute the work. Implementation (₹1L-₹1.8L with discount): Full hands-on execution including gap analysis, system design, process documentation, team training, system deployment, testing, go-live support, and post-implementation assistance. We do the work with your team.",
    relatedQuestions: [11, 12, 110],
    cta: { text: "Compare Services", link: "/pricing" }
  },
  {
    id: 113,
    category: "pricing",
    question: "What's included in the audit service?",
    keywords: ["audit", "what audit", "assessment", "gap analysis", "evaluation"],
    answer: "Audit Services (₹50K-₹2.4L): Comprehensive evaluation including current state assessment, gap analysis against standards/best practices, compliance review, risk identification, process effectiveness evaluation, documentation review, interviews with stakeholders, detailed findings report with recommendations, prioritized action plan, and audit preparation support. ISO audits, process audits, and compliance audits available.",
    relatedQuestions: [5, 6, 45, 112],
    cta: { text: "Learn About Audits", link: "/pricing" }
  },
  {
    id: 114,
    category: "pricing",
    question: "Can I get a customized package for my specific needs?",
    keywords: ["custom", "customize", "package", "tailored", "specific needs", "bespoke"],
    answer: "Absolutely! We specialize in customized solutions. Process: 1) Free consultation to understand your unique requirements, 2) Tailored proposal with mix-and-match services, 3) Flexible pricing based on scope, 4) Phased delivery options, 5) Scalable engagement models. Popular custom packages: Lean + Six Sigma combo, ISO + Process Excellence bundle, AI Strategy + RPA Implementation, Multi-site rollout programs. Every business is unique - your solution should be too!",
    relatedQuestions: [110, 12, 99],
    cta: { text: "Request Custom Package", link: "/book-consultation" }
  },

  // COMMUNITY SECTION Questions
  {
    id: 115,
    category: "community",
    question: "What is the Community section for?",
    keywords: ["community", "what is community", "community section", "forum", "discussion"],
    answer: "Our Community section is a collaborative platform for professionals interested in process excellence, AI/automation, and operational transformation. Features include: Discussion Forums (ask questions, share insights), Knowledge Sharing (case studies, best practices), Networking (connect with like-minded professionals), Resource Library (templates, guides, tools), Event Announcements (webinars, workshops), and Peer Learning. It's a space to learn, grow, and stay updated on industry trends.",
    relatedQuestions: [116, 117, 118],
    cta: { text: "Join Community", link: "/community" }
  },
  {
    id: 116,
    category: "community",
    question: "How do I join the community?",
    keywords: ["join community", "sign up", "register", "become member", "community access"],
    answer: "Joining is simple and FREE! 1) Click 'Join Community' or 'Sign Up', 2) Create your account with email/LinkedIn, 3) Complete your professional profile, 4) Agree to community guidelines, 5) Start posting, asking questions, and engaging! All professionals welcome - whether you're exploring process improvement or a seasoned expert. Active participation encouraged to get maximum value from the community.",
    relatedQuestions: [115, 117, 100],
    cta: { text: "Create Account", link: "/community" }
  },
  {
    id: 117,
    category: "community",
    question: "What can I do in the community?",
    keywords: ["community features", "what can i do", "community activities", "forum features"],
    answer: "Community Features: ✅ Post Questions & Get Answers, ✅ Share Your Experiences & Insights, ✅ Upload Images & Documents, ✅ Like & Comment on Posts, ✅ Connect with Professionals, ✅ Access Shared Resources, ✅ Participate in Discussions, ✅ Follow Topics of Interest, ✅ Get Notified of Responses, ✅ Search Archives. Moderated environment ensures quality discussions and respectful interactions.",
    relatedQuestions: [115, 116, 118],
    cta: { text: "Explore Community", link: "/community" }
  },
  {
    id: 118,
    category: "community",
    question: "Are there any community guidelines or rules?",
    keywords: ["guidelines", "rules", "community rules", "code of conduct", "moderation"],
    answer: "Community Guidelines: Be Respectful (professional and courteous communication), Stay On-Topic (process excellence, AI, automation, business improvement), No Spam or Self-Promotion (unless relevant value-add), Protect Privacy (no confidential business information), Quality Content (thoughtful contributions), Give Credit (cite sources, acknowledge others), Report Issues (flag inappropriate content). Moderation ensures safe, productive environment. Violations may result in content removal or account suspension.",
    relatedQuestions: [115, 117],
    cta: { text: "Read Full Guidelines", link: "/community" }
  },

  // MORE GENERAL & PRACTICAL Questions
  {
    id: 119,
    category: "general",
    question: "How long does a typical project take?",
    keywords: ["timeline", "duration", "how long", "project length", "timeframe"],
    answer: "Project timelines vary by scope: Quick Wins (1-2 months): Process audits, assessments, focused kaizen events. Standard Projects (3-6 months): Lean implementation, Six Sigma projects, RPA deployments. Major Initiatives (6-12 months): ISO certification, enterprise-wide transformation, comprehensive AI strategy. Implementation phases can be staggered to match your pace and resources. We provide detailed timeline estimates in proposals with key milestones and deliverables.",
    relatedQuestions: [6, 110, 99],
    cta: { text: "Discuss Timeline", link: "/book-consultation" }
  },
  {
    id: 120,
    category: "general",
    question: "Do you provide post-implementation support?",
    keywords: ["support", "post implementation", "after project", "maintenance", "follow up"],
    answer: "Yes! Comprehensive post-implementation support included: Initial Support Period (30-90 days included in project), Performance Monitoring (track results against targets), Issue Resolution (address implementation challenges), Knowledge Transfer Sessions (ensure team capability), Documentation Updates (refine based on real usage), Audit Support (for ISO certifications). Extended support and retainer agreements available for ongoing optimization and continuous improvement.",
    relatedQuestions: [110, 119, 120],
    cta: { text: "Learn About Support", link: "/book-consultation" }
  },
  {
    id: 121,
    category: "general",
    question: "Can you work remotely or do you need to be on-site?",
    keywords: ["remote", "online", "virtual", "on-site", "location", "visit"],
    answer: "Flexible Engagement Models: Remote/Virtual (video calls, online collaboration, document sharing - cost-effective for consulting and training), Hybrid (combination of remote and periodic on-site visits - most popular), Fully On-Site (for hands-on implementation requiring physical presence). Most projects work well with hybrid approach - remote for planning/documentation, on-site for workshops/go-live. Location-independent services with pan-India and international capability.",
    relatedQuestions: [99, 109, 119],
    cta: { text: "Discuss Engagement Model", link: "/book-consultation" }
  },
  {
    id: 122,
    category: "general",
    question: "What if I'm not satisfied with the results?",
    keywords: ["guarantee", "satisfaction", "refund", "not happy", "quality assurance"],
    answer: "Quality Assurance Commitment: 1) Clear Success Criteria - defined upfront with measurable targets, 2) Milestone Reviews - regular checkpoints to ensure alignment, 3) Transparent Communication - open discussion of issues as they arise, 4) Course Correction - flexible approach to address concerns, 5) 95% Success Rate - proven track record of client satisfaction. While we don't offer money-back guarantees due to consulting nature, we work diligently until you achieve agreed outcomes. Client references available upon request.",
    relatedQuestions: [103, 104, 110],
    cta: { text: "See Success Stories", link: "/#services" }
  },
  {
    id: 123,
    category: "general",
    question: "Do you sign NDAs or confidentiality agreements?",
    keywords: ["nda", "confidentiality", "non disclosure", "privacy", "security"],
    answer: "Absolutely! Professional confidentiality is paramount. We readily sign: Non-Disclosure Agreements (NDAs), Confidentiality Agreements, Data Protection Agreements, and include confidentiality clauses in all service contracts. Your business information, processes, data, and strategic plans are completely protected. We follow strict professional ethics and have 21+ years of unblemished confidentiality track record. Templates available or we can work with your legal team's documents.",
    relatedQuestions: [99, 110, 122],
    cta: { text: "Discuss Confidentiality", link: "/book-consultation" }
  }
];

// Helper function to get all questions by category
export const getQuestionsByCategory = (category) => {
  return faqDatabase.filter(faq => faq.category === category);
};

// Helper function to get popular questions
export const getPopularQuestions = () => {
  return faqDatabase.slice(0, 8);
};

// Helper function to get related questions
export const getRelatedQuestions = (questionId) => {
  const question = faqDatabase.find(q => q.id === questionId);
  if (!question || !question.relatedQuestions) return [];
  return question.relatedQuestions.map(id => 
    faqDatabase.find(q => q.id === id)
  ).filter(Boolean);
};
